import { useState } from "react";
import { IoChevronDown } from "react-icons/io5";
import {
    LabelValue,
    ListSection,
    billingRateRowToCells,
    buildBillingRateRows,
    getBillingRateHeaders,
} from "./BillingRateBuilderShared";
import { formatSubmitLineAction, formatSubmitLineDetail } from "../../utils/billingRateUtils";

const getDefMeta = (def) => ({
    name: def?.componentReference?.component ?? "",
    lockDate: def?.componentReference?.lockDate ?? "",
});

const SubmitPlanRateDetails = ({ rateDetails, componentsByName }) => {
    if (!rateDetails?.billingCategoryRates?.length) return null;

    return (
        <div className="billing-rate-builder__submit-rate-details">
            <p className="sub-title billing-rate-builder__submit-rate-categories-title">
                Billing Category Rates
            </p>

            {(rateDetails.categories ?? []).map((cat, idx) => {
                const { rowsByDef, anyTiered } = buildBillingRateRows(
                    cat.billingRateDefinitions,
                    componentsByName,
                    getDefMeta,
                );
                const headers = getBillingRateHeaders(anyTiered);
                const rows = rowsByDef.flat().map((r) => billingRateRowToCells(r, anyTiered));
                const componentSummary = (cat.components ?? [])
                    .map((c) => c.name)
                    .filter(Boolean)
                    .join(", ");

                return (
                    <div key={idx} className="billing-rate-builder__submit-rate-category">
                        <p className="sub-title billing-rate-builder__branch-title">{cat.categoryLabel}</p>
                        {componentSummary && (
                            <p className="billing-rate-builder__submit-rate-category-components">
                                <strong>Components:</strong> {componentSummary}
                            </p>
                        )}
                        {cat.partialIntervalCalculationMethod && (
                            <LabelValue
                                label="Partial Interval Calculation Method"
                                value={cat.partialIntervalCalculationMethod}
                            />
                        )}
                        {rows.length > 0 ? (
                            <ListSection
                                label="Billing Rates"
                                headers={headers}
                                rows={rows}
                            />
                        ) : (
                            <p className="billing-rate-builder__list-empty">No billing rate definitions.</p>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const BillingRateBuilderSubmitResults = ({
    planGroups,
    componentsByName,
}) => {
    const [expandedPlanIds, setExpandedPlanIds] = useState(() => new Set());

    const togglePlan = (planId) => {
        setExpandedPlanIds((prev) => {
            const next = new Set(prev);
            if (next.has(planId)) next.delete(planId);
            else next.add(planId);
            return next;
        });
    };

    if ((planGroups?.length ?? 0) === 0) {
        return <p className="billing-rate-builder__list-empty">No submission line items to display.</p>;
    }

    return (
        <div className="billing-rate-builder__submit-results-by-plan">
            {(planGroups ?? []).map((plan) => {
                const isExpanded = expandedPlanIds.has(plan.planId);
                const rateAction = formatSubmitLineAction(plan.rateLine?.action);
                const rateDetail = formatSubmitLineDetail(plan.rateLine);
                const categoryCount = plan.rateDetails?.categories?.length ?? 0;

                return (
                    <div key={plan.planId} className="billing-rate-builder__submit-plan-section">
                        <button
                            type="button"
                            className="billing-rate-builder__section-divider-btn billing-rate-builder__submit-plan-header"
                            onClick={() => togglePlan(plan.planId)}
                            aria-expanded={isExpanded}
                        >
                            <IoChevronDown
                                className={`billing-rate-builder__expand-chevron${
                                    isExpanded ? " billing-rate-builder__expand-chevron--open" : ""
                                }`}
                            />
                            <span className="billing-rate-builder__submit-plan-label">{plan.planLabel}</span>
                            {categoryCount > 0 && (
                                <span className="billing-rate-builder__section-count">{categoryCount}</span>
                            )}
                        </button>
                        {isExpanded && (
                            <div className="billing-rate-builder__submit-plan-body">
                                <p className="billing-rate-builder__submit-plan-rate-line">
                                    <strong>Billing rate:</strong> {rateAction}
                                    {rateDetail !== "—" && (
                                        <span className="billing-rate-builder__muted"> — {rateDetail}</span>
                                    )}
                                </p>
                                <SubmitPlanRateDetails
                                    rateDetails={plan.rateDetails}
                                    componentsByName={componentsByName}
                                />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default BillingRateBuilderSubmitResults;
